/**
 * ProgramConfigurePage — /programs/:id/configure
 *
 * Four-step wizard (Audiences → Time Frames → Invitation Text → Publishing)
 * that mirrors the course /configure page (Figma `11126:813563`). Programs
 * differ from courses in three ways the page absorbs:
 *
 * 1. Audiences are first-class persisted entities, not fresh rows on every
 *    save. The page diffs client state against the server snapshot on save:
 *    POST new ones, PUT modified ones, DELETE removed ones, and POST/DELETE
 *    on the per-audience `/learners` sub-endpoints to keep the roster in sync.
 * 2. The audience schema accepts `learner_ids` only — no `group_ids`. The
 *    Add-Learners modal still exposes "Add by Role" and "Add by Location"
 *    (those resolve to learner ids), but the "Add individuals" tab doesn't
 *    show the courses-style group multi-select.
 * 3. The audience schema does not accept `channel` or `invite_message`. Step
 *    3 (Invitation Text) renders for design parity with the courses page,
 *    surfaced inline via an ApiGapNotice — the values are dropped on save.
 */

import { EyeSlashIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import toast, { Toaster } from 'react-hot-toast';
import { EASE_OUT } from '@/lib/motion';
import ComposeText from '@/components/configure/ComposeText';
import ChannelSelect from '@/components/configure/ChannelSelect';
import { useSidebar } from '@/contexts/SidebarContext';
import {
  useProgram,
  fetchAudience,
  useCreateAudience,
  useUpdateAudience,
  useDeleteAudience,
  useAddAudienceLearners,
  useRemoveAudienceLearners,
  useSendAudience,
  useUpdateProgramStatus,
} from '../../services/programs';
import {
  useLearnerRoles,
  useLearnerDepartments,
  useLearnerLocations,
  fetchLearners,
} from '../../services/learners';
import PublishStatusControl from '../../components/common/PublishStatusControl';
import { useAssignableLearners } from '../../hooks/useAssignableLearners';
import type {
  Learner,
  LearnerRole,
  LearnerDepartment,
  LearnerLocation,
} from '../../types/learner';
import type { ProgramAudience } from '../../types/program';
import type { AssignChannel } from '../../types/assignment';
import StepCard from '../courses/wizard/shared/StepCard';
import WizardStepIndicator from '../courses/wizard/WizardStepIndicator';
import type { WizardStepNumber as IndicatorStepNumber, WizardStepState } from '../courses/wizard/wizardTypes';
import { useWizardState } from '../courses/wizard/useWizardState';
import { useDismissible } from '@/hooks/learner/useMemberConfig';
import { palette } from '@/styles/figma-tokens';
import CsvLearnerImport from '@/components/learners/CsvLearnerImport';
import { usePlanFeatures } from '@/hooks/usePlanFeatures';

const FONT = 'Poppins, sans-serif';
const PURPLE = 'rgb(var(--color-primary))';

type ProgramStep = 1 | 2 | 3 | 4;

const STEP_LABELS: Record<IndicatorStepNumber, string> = {
  1: 'Audiences',
  2: 'Time Frames',
  3: 'Invitation Text',
  4: 'Publishing',
};

// ─── Audience draft model (client-side, diff-and-persist on save) ────────────

interface ProgramAudienceDraft {
  id: string;             // stable client id (= serverId once persisted)
  serverId: string | null; // null → never persisted; create on save
  removed: boolean;        // local tombstone — DELETE on save
  // Send state: true once at least one learner has been notified. A sent
  // audience keeps its invitation (dates, channel, message) but its PEOPLE
  // stay editable — matching the course flow (Aleix, 2026-09-08).
  sent: boolean;
  /** True once this audience's members have been fetched (server audiences). */
  membersLoaded: boolean;

  // Editable fields
  name: string;
  learnerIds: Set<string>;
  startDate: string;
  endDate: string;
  publishDate: string;

  // Diff baselines (so we know what to PUT/POST/DELETE on save)
  initialName: string;
  initialLearnerIds: Set<string>;
  initialStartDate: string;
  initialEndDate: string;
  initialPublishDate: string;
  initialInvitationChannels: AssignChannel[];
  initialInvitationHtml: string;

  // Step 3 — notification channels + invite message.
  invitationChannels: AssignChannel[];
  invitationHtml: string;
}

const newAudienceDraft = (index: number): ProgramAudienceDraft => ({
  id: `aud-${Math.random().toString(36).slice(2, 9)}-${Date.now().toString(36).slice(-3)}`,
  serverId: null,
  removed: false,
  sent: false,
  membersLoaded: true,
  name: `Audience ${index}`,
  learnerIds: new Set(),
  startDate: '',
  endDate: '',
  publishDate: '',
  initialName: '',
  initialLearnerIds: new Set(),
  initialStartDate: '',
  initialEndDate: '',
  initialPublishDate: '',
  initialInvitationChannels: ['EMAIL'],
  initialInvitationHtml: '',
  invitationChannels: ['EMAIL'],
  invitationHtml: '',
});

const fromServer = (a: ProgramAudience): ProgramAudienceDraft => ({
  id: a.id,
  serverId: a.id,
  removed: false,
  // Sent once at least one learner has been notified (pending < total).
  sent: a.learners_count > 0 && a.pending_count < a.learners_count,
  name: a.name ?? 'Audience',
  membersLoaded: false,        // filled by the follow-up fetch below
  learnerIds: new Set(),
  startDate: a.start_date ?? '',
  endDate: a.end_date ?? '',
  publishDate: a.publish_date ?? '',
  initialName: a.name ?? 'Audience',
  initialLearnerIds: new Set(),
  initialStartDate: a.start_date ?? '',
  initialEndDate: a.end_date ?? '',
  initialPublishDate: a.publish_date ?? '',
  initialInvitationChannels: a.channels && a.channels.length ? a.channels : ['EMAIL'],
  initialInvitationHtml: a.invite_message ?? '',
  invitationChannels: a.channels && a.channels.length ? a.channels : ['EMAIL'],
  invitationHtml: a.invite_message ?? '',
});

// ─── Inline icons ────────────────────────────────────────────────────────────

const PlusIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);
const PenIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15.232 5.232l3.536 3.536M16.707 3.293a2 2 0 012.828 0l1.172 1.172a2 2 0 010 2.828L7.5 20.5H3v-4.5L16.707 3.293z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const UserPlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="9" cy="8" r="3.5" stroke="currentColor" strokeWidth="2" />
    <path d="M3 21c0-3.3 2.7-6 6-6s6 2.7 6 6M19 8v6M16 11h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const CheckIcon = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const XIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const UploadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const TrashSmallIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M14.74 9l-.346 9m-4.788 0L9.26 9M5 6h14m-2 0l-1.5 14h-7L4 6m5 0V4a1 1 0 011-1h4a1 1 0 011 1v2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const EyeOpenIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
  </svg>
);

// ─── Pill button (theme-aware, press feedback) ──────────────────────────────

interface PillButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'purple' | 'dark' | 'green' | 'orange-outline' | 'ghost';
  type?: 'button' | 'submit';
  disabled?: boolean;
  className?: string;
  onMouseDown?: React.MouseEventHandler<HTMLButtonElement>;
}

function PillButton({ children, onClick, variant = 'purple', type = 'button', disabled, className = '', onMouseDown }: PillButtonProps) {
  const base = 'inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-bold transition-[filter,background-color,transform,color] duration-150 active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed';
  const variants: Record<NonNullable<PillButtonProps['variant']>, string> = {
    purple: 'text-white hover:brightness-105',
    dark: 'bg-grey-900 dark:bg-grey-100 text-white dark:text-grey-900 hover:brightness-105',
    green: 'bg-success-500 text-white hover:brightness-105',
    'orange-outline': 'bg-transparent text-secondary border border-secondary hover:bg-secondary/5',
    ghost: 'bg-transparent text-text-primary border border-text-primary/30 hover:bg-text-primary/[0.06]',
  };
  const inlineStyle: React.CSSProperties = { fontFamily: FONT };
  if (variant === 'purple') {
    inlineStyle.background = `linear-gradient(to top, ${'rgb(var(--color-primary-700))'}, rgb(var(--color-primary)) 70%)`;
  }
  return (
    <button type={type} onClick={onClick} onMouseDown={onMouseDown} disabled={disabled} className={`${base} ${variants[variant]} ${className}`} style={inlineStyle}>
      {children}
    </button>
  );
}

// ─── Reminder card + collapsible right column ───────────────────────────────

function Reminder({ title, body, onDismiss }: { title: string; body: React.ReactNode; onDismiss: () => void }) {
  return (
    <div
      className="relative rounded-2xl px-7 py-7 border h-full"
      style={{ background: 'rgb(var(--color-secondary) / 0.08)', borderColor: 'rgb(var(--color-secondary) / 0.30)' }}
    >
      <button
        type="button"
        aria-label="Dismiss reminder"
        onClick={onDismiss}
        className="absolute top-3 right-3 w-7 h-7 rounded-full border inline-flex items-center justify-center hover:bg-text-tertiary/5 active:scale-[0.96] transition-[transform,background-color] duration-150"
        style={{ borderColor: 'rgb(var(--color-secondary) / 0.45)', color: 'rgb(var(--color-secondary))' }}
      >
        <XIcon size={12} />
      </button>
      <h3 className="text-lg font-bold text-text-primary text-center mb-3" style={{ fontFamily: FONT }}>{title}</h3>
      <div className="text-sm text-text-secondary leading-relaxed text-center" style={{ fontFamily: FONT }}>{body}</div>
      <div className="flex justify-center mt-5">
        <button
          type="button"
          onClick={onDismiss}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border border-text-primary/30 text-text-primary hover:bg-text-tertiary/5 active:scale-[0.97] transition-[transform,background-color] duration-150"
        >
          <EyeSlashIcon className="w-4 h-4" aria-hidden="true" /> Never show it again
        </button>
      </div>
    </div>
  );
}

function ReminderLayout({ reminderVisible, reminderNode, children }: { reminderVisible: boolean; reminderNode: React.ReactNode; children: React.ReactNode }) {
  const reduceMotion = useReducedMotion();
  return (
    <div className="flex flex-col lg:flex-row gap-6 items-stretch">
      <div className="flex-1 min-w-0">{children}</div>
      <AnimatePresence initial={false}>
        {reminderVisible && (
          <motion.aside
            key="reminder"
            initial={reduceMotion ? false : { opacity: 0 }}
            // Only animate opacity on enter — the column width comes from the
            // Tailwind classes below. (Setting `width: 'auto'` here overrides
            // the `lg:w-[320px]` rule and lets the aside expand to the natural
            // width of the reminder card, eating the main column.)
            animate={{ opacity: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, width: 0, marginLeft: -24 }}
            transition={{ duration: 0.25, ease: EASE_OUT }}
            className="w-full lg:w-[320px] shrink-0 overflow-hidden"
          >
            {reminderNode}
          </motion.aside>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Reminder dismissal backed by the member's `/me/config`; treat "still loading
 * config" as hidden so a previously-dismissed reminder never flashes.
 */
function useReminderDismiss(storageKey: string) {
  const { dismissed, isLoading, dismiss } = useDismissible(storageKey);
  return { dismissed: isLoading || dismissed, dismiss };
}

// ─── Locked overlay ─────────────────────────────────────────────────────────

function LockedOverlay({ message }: { message: string }) {
  return (
    <div className="absolute inset-0 z-20 rounded-2xl bg-background-primary/65 dark:bg-grey-900/65 backdrop-blur-[2px] flex items-center justify-center cursor-not-allowed">
      <span
        className="text-[13px] font-semibold text-text-secondary bg-background-primary border border-border-primary/20 rounded-lg px-3.5 py-1.5 shadow-sm"
        style={{ fontFamily: FONT }}
      >
        {message}
      </span>
    </div>
  );
}

// ─── Audience tabs (used by Step 2/3/4) ─────────────────────────────────────

interface AudienceTabsProps {
  audiences: ProgramAudienceDraft[];
  activeId: string | null;
  onSelect: (id: string) => void;
  extraTab?: { label: string; id: string };
}

function AudienceTabs({ audiences, activeId, onSelect, extraTab }: AudienceTabsProps) {
  const visible = audiences.filter((a) => !a.removed);
  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: extraTab ? `repeat(${visible.length}, 1fr) auto` : `repeat(${visible.length}, minmax(0, 1fr))` }}>
      {visible.map((aud) => {
        const active = aud.id === activeId;
        return (
          <button
            key={aud.id}
            type="button"
            onClick={() => onSelect(aud.id)}
            className={`h-11 rounded-full text-sm font-bold transition-[background-color,color,transform] duration-150 active:scale-[0.98] ${active ? 'bg-primary-500 ' : 'bg-text-tertiary/10 dark:bg-text-tertiary/15 text-text-primary'}`}
            style={{ fontFamily: FONT, letterSpacing: '0.04em' }}
          >
            {aud.name.toUpperCase()}
          </button>
        );
      })}
      {extraTab && (
        <button
          type="button"
          onClick={() => onSelect(extraTab.id)}
          className={`h-11 px-5 rounded-full text-sm font-bold transition-[background-color,color,transform] duration-150 active:scale-[0.98] ${activeId === extraTab.id ? 'bg-primary-500 ' : 'bg-text-tertiary/10 dark:bg-text-tertiary/15 text-text-primary'}`}
          style={{ fontFamily: FONT, letterSpacing: '0.04em' }}
        >
          {extraTab.label}
        </button>
      )}
    </div>
  );
}

// ─── Audience card (Step 1, with inline rename + per-card actions) ───────────

/** Chips shown before the list folds behind a "+N more" toggle. */
const MEMBER_CHIP_LIMIT = 8;

interface AudienceCardProps {
  audience: ProgramAudienceDraft;
  index: number;
  editable: boolean;
  /** Directory used to name the people shown on the card. */
  learners: Learner[];
  onAddLearners: () => void;
  onRename: (name: string) => void;
  onRemove: () => void;
  onRemoveLearner: (learnerId: string) => void;
}

function AudienceCard({
  audience,
  index,
  editable,
  learners,
  onAddLearners,
  onRename,
  onRemove,
  onRemoveLearner,
}: AudienceCardProps) {
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(audience.name);
  const inputRef = useRef<HTMLInputElement>(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!editing) setDraftName(audience.name);
  }, [audience.name, editing]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = () => {
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== audience.name) onRename(trimmed);
    else setDraftName(audience.name);
    setEditing(false);
  };

  const cancel = () => {
    setDraftName(audience.name);
    setEditing(false);
  };

  const learnerCount = audience.learnerIds.size;
  const empty = learnerCount === 0;

  // Who is in this audience — visible and removable in one click, matching the
  // course configure page (Aleix, 2026-09-08). Ids with no match still render,
  // so a learner removed from the workspace mid-draft can be seen and cleared.
  const members = useMemo(() => {
    const learnerById = new Map(learners.map((l) => [l.id, l]));
    return Array.from(audience.learnerIds).map((id) => {
      const l = learnerById.get(id);
      return {
        id,
        name: l ? l.full_name || `${l.first_name} ${l.last_name}`.trim() || l.email : 'Unknown learner',
      };
    });
  }, [audience.learnerIds, learners]);

  const visibleMembers = showAllMembers ? members : members.slice(0, MEMBER_CHIP_LIMIT);
  const hiddenCount = showAllMembers ? 0 : members.length - visibleMembers.length;

  return (
    <motion.li
      layout
      initial={reduceMotion ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.98 }}
      transition={{ duration: 0.22, ease: EASE_OUT, delay: reduceMotion ? 0 : index * 0.04 }}
      className="group rounded-xl bg-text-tertiary/10 dark:bg-text-tertiary/15 hover:bg-text-tertiary/15 dark:hover:bg-text-tertiary/20 transition-[background-color] duration-150"
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 px-5 py-4">
        <div className="flex-1 min-w-0">
          {editing ? (
            <input
              ref={inputRef}
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commit(); }
                else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
              }}
              maxLength={60}
              aria-label="Audience name"
              className="w-full bg-background-primary border border-primary-500/50 rounded-lg px-3 py-1.5 text-base font-extrabold text-text-primary outline-none focus:border-primary-500 transition-colors"
              style={{ fontFamily: FONT, letterSpacing: '0.02em' }}
            />
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              <p className="text-base font-extrabold text-text-primary leading-6 truncate" style={{ fontFamily: FONT, letterSpacing: '0.02em' }}>
                {audience.name.toUpperCase()}
              </p>
              {audience.sent && (
                <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-success-500/15 text-success-600">
                  Sent
                </span>
              )}
            </div>
          )}
          <p className={`text-xs mt-1 ${empty ? 'text-text-tertiary italic' : 'text-text-secondary'}`} style={{ fontFamily: FONT }}>
            {empty ? 'No learners yet' : `${learnerCount} ${learnerCount === 1 ? 'learner' : 'learners'}`}
          </p>

          {members.length > 0 && (
            <ul className="mt-2.5 flex flex-wrap gap-1.5">
              {visibleMembers.map((m) => (
                <li key={m.id}>
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary-500/10 py-1 pl-2.5 pr-1 text-xs font-semibold text-primary-700 dark:text-primary-300">
                    <span className="max-w-[180px] truncate">{m.name}</span>
                    {editable && (
                      <button
                        type="button"
                        onClick={() => onRemoveLearner(m.id)}
                        aria-label={`Remove ${m.name} from this audience`}
                        title={`Remove ${m.name}`}
                        className="flex h-4 w-4 items-center justify-center rounded-full transition-colors hover:bg-text-primary/15 focus-ring"
                      >
                        <XIcon size={10} />
                      </button>
                    )}
                  </span>
                </li>
              ))}
              {hiddenCount > 0 && (
                <li>
                  <button
                    type="button"
                    onClick={() => setShowAllMembers(true)}
                    className="rounded-full px-2.5 py-1 text-xs font-bold text-text-secondary underline underline-offset-2 hover:text-text-primary focus-ring transition-colors"
                  >
                    +{hiddenCount} more
                  </button>
                </li>
              )}
              {showAllMembers && members.length > MEMBER_CHIP_LIMIT && (
                <li>
                  <button
                    type="button"
                    onClick={() => setShowAllMembers(false)}
                    className="rounded-full px-2.5 py-1 text-xs font-bold text-text-secondary underline underline-offset-2 hover:text-text-primary focus-ring transition-colors"
                  >
                    Show less
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>
        {editable && (
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <PillButton variant="green" onClick={onAddLearners}>
              <UserPlusIcon /> Add Learners
            </PillButton>
            <PillButton
              variant="dark"
              onClick={() => (editing ? commit() : setEditing(true))}
              onMouseDown={editing ? (e) => e.preventDefault() : undefined}
            >
              {editing ? <CheckIcon size={13} /> : <PenIcon />}
              {/* "Edit" only ever renamed — the chips above own removing people. */}
              {editing ? 'Save' : 'Rename'}
            </PillButton>
            <button
              type="button"
              aria-label={`Remove ${audience.name}`}
              onClick={() => {
                // A sent audience's learners lose access when it goes —
                // say so before, not after (Aleix, 2026-09-08).
                const message = audience.sent
                  ? `Remove ${audience.name}? Its ${learnerCount} learner${learnerCount === 1 ? '' : 's'} lose access to this program (their progress is kept).`
                  : `Remove ${audience.name}? Pending changes will be lost.`;
                if (window.confirm(message)) onRemove();
              }}
              title="Remove audience"
              className="inline-flex items-center justify-center w-8 h-8 rounded-full text-text-disabled hover:text-error-500 hover:bg-error-500/10 active:scale-[0.92] transition-[background-color,color,transform] duration-150 opacity-0 group-hover:opacity-100 focus:opacity-100"
            >
              <TrashSmallIcon />
            </button>
          </div>
        )}
      </div>
    </motion.li>
  );
}

// ─── Step 1: Audiences ──────────────────────────────────────────────────────

function Step1Audiences({
  audiences,
  learners,
  onAddAudience,
  onAddLearners,
  onRenameAudience,
  onRemoveAudience,
  onRemoveLearner,
}: {
  audiences: ProgramAudienceDraft[];
  learners: Learner[];
  onAddAudience: () => void;
  onAddLearners: (id: string) => void;
  onRenameAudience: (id: string, name: string) => void;
  onRemoveAudience: (id: string) => void;
  onRemoveLearner: (audienceId: string, learnerId: string) => void;
}) {
  const visible = audiences.filter((a) => !a.removed);
  const { dismissed, dismiss } = useReminderDismiss('program-configure-reminder-step1-dismissed');
  return (
    <ReminderLayout
      reminderVisible={!dismissed}
      reminderNode={
        <Reminder
          title="Reminder"
          onDismiss={dismiss}
          body={
            <>
              <p>Audiences let you launch this program to different groups of learners at different times.</p>
              <p className="mt-3">Each audience has its own date window. You can add as many as you need; learners receive an invitation when the audience is published.</p>
            </>
          }
        />
      }
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-2xl border-2 border-dashed border-primary-500 px-5 py-5 min-h-[200px] flex flex-col gap-3" style={{ borderStyle: 'dashed' }}>
          {visible.length === 0 ? (
            <div className="flex flex-col items-center gap-1 py-10 text-center" style={{ fontFamily: FONT }}>
              <p className="text-sm font-semibold text-text-primary">No Audiences Yet</p>
              <p className="text-xs text-text-secondary max-w-[44ch]">
                Create your first audience below to start choosing which learners receive this program and when.
              </p>
            </div>
          ) : (
            <ul className="flex flex-col gap-3">
              <AnimatePresence initial={false}>
                {visible.map((aud, i) => (
                  <AudienceCard
                    key={aud.id}
                    audience={aud}
                    index={i}
                    learners={learners}
                    onRemoveLearner={(learnerId) => onRemoveLearner(aud.id, learnerId)}
                    editable
                    onAddLearners={() => onAddLearners(aud.id)}
                    onRename={(name) => onRenameAudience(aud.id, name)}
                    onRemove={() => onRemoveAudience(aud.id)}
                  />
                ))}
              </AnimatePresence>
            </ul>
          )}
        </div>
        <div className="flex justify-center">
          <PillButton variant="purple" onClick={onAddAudience}>
            <PlusIcon size={14} /> Add Audience
          </PillButton>
        </div>
      </div>
    </ReminderLayout>
  );
}

// ─── Step 2: Time Frames ────────────────────────────────────────────────────

/** Add `days` to a 'YYYY-MM-DD' string, returning 'YYYY-MM-DD' (UTC-safe). */
function addDaysISO(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '';
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

function DateField({ id, label, value, min, onChange, disabled, hint }: { id: string; label: string; value: string; min?: string; onChange?: (v: string) => void; disabled?: boolean; hint?: string }) {
  return (
    <div>
      <div className="relative">
        <label htmlFor={id} className="absolute -top-2 left-4 px-1 text-xs font-semibold text-text-primary bg-background-primary z-10" style={{ fontFamily: FONT }}>
          {label}
        </label>
        <input
          id={id}
          type="date"
          value={value}
          min={min}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.value)}
          className="w-full h-12 px-4 rounded-2xl border border-text-primary/30 bg-background-primary text-text-primary outline-none focus:border-primary-500 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ fontFamily: FONT }}
        />
      </div>
      {hint && (
        <p className="mt-1.5 px-1 text-xs text-text-tertiary" style={{ fontFamily: FONT }}>
          {hint}
        </p>
      )}
    </div>
  );
}

function Step2TimeFrames({ audiences, activeId, setActiveId, patchAudience, durationDays }: { audiences: ProgramAudienceDraft[]; activeId: string; setActiveId: (id: string) => void; patchAudience: (id: string, patch: Partial<ProgramAudienceDraft>) => void; durationDays: number }) {
  const visible = audiences.filter((a) => !a.removed);
  const active = visible.find((a) => a.id === activeId);

  // The end date belongs to the trainer. Picking a start date DEFAULTS it to
  // start + (duration_days − 1) — the minimum the API accepts (the window
  // must fit the program) — but it may be extended freely. End dates are
  // visibility/reporting only; they never block learners.
  const startDate = active?.startDate ?? '';
  const derivedEnd = startDate ? addDaysISO(startDate, durationDays - 1) : '';

  if (!active) return null;
  // Dates stay editable after sending — only the invitation itself is
  // fixed (Aleix, 2026-09-08; matches how a sent course invitation
  // keeps its schedule editable).
  const locked = false;
  const dayWord = durationDays === 1 ? 'day' : 'days';

  return (
    <div className="flex flex-col gap-6">
      <p className="text-center text-sm text-text-secondary" style={{ fontFamily: FONT }}>
        This program runs <span className="font-semibold text-text-primary">{durationDays} {dayWord}</span>.
        Pick a start date and end date for each audience. The end date defaults
        to the program length and can be extended.
      </p>
      <AudienceTabs audiences={visible} activeId={activeId} onSelect={setActiveId} />
      <div className="relative">
        {locked && <LockedOverlay message="This invitation has already been sent — its channel and message are fixed." />}
        <div className="rounded-2xl border-2 border-dashed border-primary-500/60 px-6 py-7" style={{ borderStyle: 'dashed' }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <DateField
              id={`start-${active.id}`}
              label="Start date"
              value={active.startDate}
              disabled={locked}
              onChange={(v) => patchAudience(active.id, { startDate: v, endDate: v ? addDaysISO(v, durationDays - 1) : '' })}
            />
            <DateField
              id={`end-${active.id}`}
              label="End date"
              value={active.endDate}
              disabled={locked || !active.startDate}
              min={derivedEnd || undefined}
              onChange={(v) => patchAudience(active.id, { endDate: v })}
              hint={
                active.startDate
                  ? `At least ${derivedEnd} — the program runs ${durationDays} ${dayWord}. Learners keep access after this date.`
                  : 'Set a start date first'
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Step 3: Invitation Text (UI-only — API gap) ────────────────────────────

function Step3Invitation({ audiences, activeId, setActiveId, patchAudience }: { audiences: ProgramAudienceDraft[]; activeId: string; setActiveId: (id: string) => void; patchAudience: (id: string, patch: Partial<ProgramAudienceDraft>) => void }) {
  const visible = audiences.filter((a) => !a.removed);
  const active = visible.find((a) => a.id === activeId);
  const { dismissed, dismiss } = useReminderDismiss('program-configure-reminder-step3-dismissed');
  if (!active) return null;
  const locked = active.sent;
  return (
    <ReminderLayout
      reminderVisible={!dismissed}
      reminderNode={
        <Reminder
          title="Reminder"
          onDismiss={dismiss}
          body={<p>The invitation text will be sent the day before the program start date.</p>}
        />
      }
    >
      <div className="flex flex-col gap-5">
        <div className="rounded-2xl border-2 border-dashed border-primary-500/60 px-6 py-6" style={{ borderStyle: 'dashed' }}>
          <h3 className="text-base font-bold text-text-primary text-center mb-4" style={{ fontFamily: FONT }}>Choose Audience</h3>
          <AudienceTabs audiences={visible} activeId={activeId} onSelect={setActiveId} />
        </div>

        <div className="relative flex flex-col gap-5">
          {locked && <LockedOverlay message="This invitation has already been sent — its channel and message are fixed." />}
          <div className="rounded-2xl border border-text-primary/15 px-6 py-5 bg-text-tertiary/10 dark:bg-text-tertiary/15">
            <h3 className="text-base font-bold text-text-primary text-center mb-1" style={{ fontFamily: FONT }}>
              How Should Learners Be Notified?
            </h3>
            <p className="text-xs text-text-secondary text-center mb-4" style={{ fontFamily: FONT }}>
              Pick at least one channel. Chat posts an invitation card in a group conversation.
            </p>
            <ChannelSelect
              value={active.invitationChannels}
              onChange={(channels) => patchAudience(active.id, { invitationChannels: channels })}
              disabled={locked}
            />
          </div>

          <ComposeText
            value={active.invitationHtml}
            onChange={(html) => patchAudience(active.id, { invitationHtml: html })}
          />
        </div>
      </div>
    </ReminderLayout>
  );
}

// ─── Step 4: Publishing ─────────────────────────────────────────────────────

function Step4Publishing({ audiences, activeId, setActiveId, patchAudience }: { audiences: ProgramAudienceDraft[]; activeId: string; setActiveId: (id: string) => void; patchAudience: (id: string, patch: Partial<ProgramAudienceDraft>) => void }) {
  const visible = audiences.filter((a) => !a.removed);
  const active = visible.find((a) => a.id === activeId);
  const { dismissed, dismiss } = useReminderDismiss('program-configure-reminder-step4-dismissed');
  if (!active) return null;
  // Dates stay editable after sending — only the invitation itself is
  // fixed (Aleix, 2026-09-08; matches how a sent course invitation
  // keeps its schedule editable).
  const locked = false;
  return (
    <div className="flex flex-col gap-6">
      <p className="text-center text-sm text-text-secondary" style={{ fontFamily: FONT }}>
        Click on an Audience, set the publish date, click Done and move to the other one.
      </p>
      <AudienceTabs audiences={visible} activeId={activeId} onSelect={setActiveId} />
      <ReminderLayout
        reminderVisible={!dismissed}
        reminderNode={
          <Reminder
            title="Reminder"
            onDismiss={dismiss}
            body={
              <>
                <p>Publishing date is when the program appears in the learners portal — that's also when they receive the invitation.</p>
                <p className="mt-3">They won't be able to start it until the start date set in Step 2.</p>
              </>
            }
          />
        }
      >
        <div className="relative">
          {locked && <LockedOverlay message="This invitation has already been sent — its channel and message are fixed." />}
          <div className="rounded-2xl border-2 border-dashed border-primary-500/60 px-6 py-7" style={{ borderStyle: 'dashed' }}>
            <DateField
              id={`publish-${active.id}`}
              label="Publish date"
              value={active.publishDate}
              min={undefined}
              disabled={locked}
              onChange={(v) => patchAudience(active.id, { publishDate: v })}
            />
          </div>
        </div>
      </ReminderLayout>
    </div>
  );
}

// ─── Add Learners modal ─────────────────────────────────────────────────────

type AddLearnersMode = null | 'individuals' | 'role' | 'department' | 'location' | 'csv';

function AddLearnersModal({
  isOpen, onClose, audience, learners, roles, departments, locations, learnersLoading, rolesLoading,
  departmentsLoading, locationsLoading, onApply,
}: {
  isOpen: boolean;
  onClose: () => void;
  audience: ProgramAudienceDraft | null;
  learners: Learner[];
  roles: LearnerRole[];
  departments: LearnerDepartment[];
  locations: LearnerLocation[];
  learnersLoading: boolean;
  rolesLoading: boolean;
  departmentsLoading: boolean;
  locationsLoading: boolean;
  onApply: (learnerIds: Set<string>) => void;
}) {
  // Attribute shortcuts and CSV import are paid features (pricing rows
  // "Attribute filters" and "Bulk tools", Growth+) — same gate the learner
  // list uses, so Free cannot reach them here either (Aleix's ripple rule, 9/8).
  const { attributeFilters, bulkTools } = usePlanFeatures();
  const [mode, setMode] = useState<AddLearnersMode>(null);
  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!isOpen || !audience) return;
    setMode(null);
    setSearch('');
    setDraft(new Set(audience.learnerIds));
  }, [isOpen, audience?.id]);

  const filteredLearners = useMemo(() => {
    if (!search) return learners;
    const q = search.toLowerCase();
    return learners.filter(
      (l) => l.full_name.toLowerCase().includes(q) || l.email.toLowerCase().includes(q)
    );
  }, [learners, search]);

  if (!audience) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15, ease: EASE_OUT }} className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />
          <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none p-4">
            <motion.div
              initial={reduceMotion ? false : { opacity: 0, scale: 0.96, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.97, y: 8 }}
              transition={{ duration: 0.18, ease: EASE_OUT }}
              className="pointer-events-auto bg-background-primary rounded-2xl w-full max-w-[480px] max-h-[88vh] overflow-y-auto"
              style={{ boxShadow: '4px 7px 28px 0px rgba(0,0,0,0.18)' }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Add learners"
            >
              <motion.div key={mode ?? 'menu'} initial={reduceMotion ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.12, ease: EASE_OUT }} className="px-7 py-7">
                {mode === null && (
                  <>
                    <h2 className="text-xl font-bold text-text-primary text-center mb-5" style={{ fontFamily: FONT }}>Add Learners</h2>
                    <div className="flex flex-col gap-3">
                      <ModeRow label="Add individuals" variant="purple" icon={<PlusIcon />} onClick={() => setMode('individuals')} />
                      {attributeFilters && (
                        <>
                          <ModeRow label="Add by Role" variant="purple" icon={<PlusIcon />} onClick={() => setMode('role')} />
                          <ModeRow label="Add by Department" variant="purple" icon={<PlusIcon />} onClick={() => setMode('department')} />
                          <ModeRow label="Add by Location" variant="purple" icon={<PlusIcon />} onClick={() => setMode('location')} />
                        </>
                      )}
                      {bulkTools && (
                        <ModeRow label="Bulk Upload with CSV" variant="dark" icon={<UploadIcon />} onClick={() => setMode('csv')} />
                      )}
                    </div>
                    <div className="flex justify-center mt-6">
                      <PillButton onClick={onClose} variant="orange-outline"><XIcon size={14} /> Cancel</PillButton>
                    </div>
                  </>
                )}

                {mode === 'individuals' && (
                  <>
                    <button type="button" onClick={() => setMode(null)} className="text-xs font-semibold text-text-secondary hover:text-text-primary active:scale-[0.97] transition-[color,transform] duration-150 mb-3">← Back</button>
                    <h2 className="text-xl font-bold text-text-primary text-center mb-4" style={{ fontFamily: FONT }}>Add Individuals</h2>
                    <div className="relative mb-3">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none"><SearchIcon /></span>
                      <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find learners…" className="w-full h-10 pl-9 pr-3 text-sm bg-background-primary border border-border-primary/25 rounded-xl outline-none placeholder:text-text-tertiary text-text-primary focus:border-primary-500 transition-colors" />
                    </div>
                    <div className="rounded-xl border border-border-primary/20 max-h-[320px] overflow-y-auto">
                      {learnersLoading ? (
                        <div className="p-3 space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-9 rounded-lg bg-text-tertiary/10 animate-pulse" />)}</div>
                      ) : filteredLearners.length === 0 ? (
                        <p className="px-4 py-6 text-center text-sm text-text-secondary">{search ? 'No learners match.' : 'No learners yet.'}</p>
                      ) : (
                        <ul className="divide-y divide-border-primary/10">
                          {filteredLearners.map((l) => {
                            const selected = draft.has(l.id);
                            return (
                              <li key={l.id}>
                                <label className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-text-tertiary/5 transition-colors">
                                  <Checkbox checked={selected} />
                                  <input type="checkbox" className="sr-only" checked={selected} onChange={() => {
                                    const next = new Set(draft);
                                    next.has(l.id) ? next.delete(l.id) : next.add(l.id);
                                    setDraft(next);
                                  }} />
                                  <span className="flex-1 min-w-0">
                                    <span className="block text-sm font-semibold text-text-primary truncate">{l.full_name}</span>
                                    <span className="block text-xs text-text-secondary truncate">{l.email}</span>
                                  </span>
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-4 mt-5">
                      <p className="text-xs text-text-secondary"><span className="font-bold text-text-primary">{draft.size}</span> selected</p>
                      <div className="flex items-center gap-3">
                        <PillButton onClick={onClose} variant="orange-outline"><XIcon size={14} /> Cancel</PillButton>
                        <PillButton onClick={() => { onApply(draft); onClose(); }} variant="purple"><CheckIcon size={14} /> Save</PillButton>
                      </div>
                    </div>
                  </>
                )}

                {(mode === 'role' || mode === 'department' || mode === 'location') && (
                  <AttributePicker
                    type={mode}
                    attributes={
                      mode === 'role' ? roles : mode === 'department' ? departments : locations
                    }
                    loading={
                      mode === 'role'
                        ? rolesLoading
                        : mode === 'department'
                          ? departmentsLoading
                          : locationsLoading
                    }
                    onAddLearners={(ids) => {
                      const next = new Set(draft);
                      ids.forEach((id) => next.add(id));
                      setDraft(next);
                    }}
                    onBack={() => setMode(null)}
                    onDone={() => { onApply(draft); onClose(); }}
                    onCancel={onClose}
                  />
                )}

                {mode === 'csv' && (
                  <CsvLearnerImport
                    onBack={() => setMode(null)}
                    onImported={(ids) => {
                      const next = new Set(draft);
                      ids.forEach((id) => next.add(id));
                      setDraft(next);
                      setMode('individuals');
                    }}
                  />
                )}
              </motion.div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

function ModeRow({ label, variant, icon, onClick }: { label: string; variant: 'purple' | 'dark'; icon: React.ReactNode; onClick: () => void }) {
  const purple = variant === 'purple';
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative h-12 rounded-full text-sm font-bold flex items-center justify-center transition-[filter,transform] duration-150 hover:brightness-105 active:scale-[0.98]"
      style={{ background: purple ? `linear-gradient(to top, ${'rgb(var(--color-primary-700))'}, rgb(var(--color-primary)) 70%)` : palette.black, color: '#fff', fontFamily: FONT }}
    >
      <span>{label}</span>
      <span className="absolute right-1 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full inline-flex items-center justify-center" style={{ background: purple ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.12)' }}>
        {icon}
      </span>
    </button>
  );
}

function Checkbox({ checked }: { checked: boolean }) {
  return (
    <span className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-md border text-white transition-[background-color,border-color] duration-150 shrink-0" style={{ background: checked ? PURPLE : 'transparent', borderColor: checked ? PURPLE : 'rgba(189,188,201,0.5)' }}>
      {checked && <CheckIcon size={11} />}
    </span>
  );
}

// ─── AttributePicker (Add by Role / Add by Location) ────────────────────────

function AttributePicker({
  type, attributes, loading, onAddLearners, onBack, onDone, onCancel,
}: {
  type: 'role' | 'department' | 'location';
  attributes: Array<LearnerRole | LearnerLocation>;
  loading: boolean;
  onAddLearners: (ids: string[]) => void;
  onBack: () => void;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [adding, setAdding] = useState<string | null>(null);
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  const reduceMotion = useReducedMotion();

  const handleAdd = async (attrId: string) => {
    setAdding(attrId);
    try {
      const result = await fetchLearners({
        per_page: 100,
        ...(type === 'role'
          ? { role_id: attrId }
          : type === 'department'
            ? { department_id: attrId }
            : { location_id: attrId }),
      });
      const ids = result.learners.map((l) => l.id);
      onAddLearners(ids);
      setAddedIds((prev) => { const next = new Set(prev); next.add(attrId); return next; });
      toast.success(`Added ${ids.length} ${ids.length === 1 ? 'learner' : 'learners'}`);
    } catch {
      toast.error(`Could not load learners for this ${type}.`);
    } finally {
      setAdding(null);
    }
  };

  return (
    <>
      <button type="button" onClick={onBack} className="text-xs font-semibold text-text-secondary hover:text-text-primary active:scale-[0.97] transition-[color,transform] duration-150 mb-3">← Back</button>
      <h2 className="text-xl font-bold text-text-primary text-center mb-4" style={{ fontFamily: FONT }}>
        {type === 'role'
          ? 'Add by Role'
          : type === 'department'
            ? 'Add by Department'
            : 'Add by Location'}
      </h2>
      <div className="rounded-xl border border-border-primary/20 max-h-[320px] overflow-y-auto">
        {loading ? (
          <div className="p-3 space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-12 rounded-lg bg-text-tertiary/10 animate-pulse" />)}</div>
        ) : attributes.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-text-secondary" style={{ fontFamily: FONT }}>
            No {type === 'role' ? 'roles' : 'locations'} defined yet.<br />
            <span className="text-xs text-text-tertiary">Add them in Settings → Learners → {type === 'role' ? 'Roles' : 'Locations'}.</span>
          </p>
        ) : (
          <ul className="divide-y divide-border-primary/10">
            {attributes.map((attr, i) => {
              const added = addedIds.has(attr.id);
              const isAdding = adding === attr.id;
              return (
                <motion.li key={attr.id} initial={reduceMotion ? false : { opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18, ease: EASE_OUT, delay: reduceMotion ? 0 : i * 0.035 }} className="flex items-center gap-3 px-4 py-3">
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-text-primary truncate" style={{ fontFamily: FONT }}>{attr.name}</span>
                    <span className="block text-xs text-text-secondary">{attr.learner_count} {attr.learner_count === 1 ? 'learner' : 'learners'}{attr.description ? ` · ${attr.description}` : ''}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => handleAdd(attr.id)}
                    disabled={isAdding || added || attr.learner_count === 0}
                    className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-[background-color,color,transform] duration-150 active:scale-[0.97] disabled:active:scale-100 ${added ? 'bg-success-500/10 text-success-500 border border-success-500/30 cursor-default' : 'bg-primary-500 hover:brightness-105 disabled:opacity-50'}`}
                    style={{ fontFamily: FONT }}
                  >
                    {isAdding ? (<><svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="animate-spin"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" /><path d="M12 3a9 9 0 019 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" /></svg> Adding…</>) : added ? (<><CheckIcon size={11} /> Added</>) : (<><PlusIcon size={11} /> Add</>)}
                  </button>
                </motion.li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="flex items-center justify-end gap-3 mt-5">
        <PillButton onClick={onCancel} variant="orange-outline"><XIcon size={14} /> Cancel</PillButton>
        <PillButton onClick={onDone} variant="purple"><CheckIcon size={14} /> Done</PillButton>
      </div>
    </>
  );
}

// ─── Audience Preview modal ─────────────────────────────────────────────────

function AudiencePreviewModal({ isOpen, onClose, audiences, learners }: { isOpen: boolean; onClose: () => void; audiences: ProgramAudienceDraft[]; learners: Learner[] }) {
  const reduceMotion = useReducedMotion();
  const learnerById = useMemo(() => {
    const m = new Map<string, Learner>();
    learners.forEach((l) => m.set(l.id, l));
    return m;
  }, [learners]);
  const visible = audiences.filter((a) => !a.removed);
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15, ease: EASE_OUT }} className="fixed inset-0 z-40 bg-black/55" onClick={onClose} />
          <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none p-4">
            <motion.div initial={reduceMotion ? false : { opacity: 0, scale: 0.96, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.97, y: 8 }} transition={{ duration: 0.18, ease: EASE_OUT }} className="pointer-events-auto bg-background-primary rounded-2xl w-full max-w-[640px] max-h-[88vh] overflow-y-auto" style={{ boxShadow: '4px 7px 28px 0px rgba(0,0,0,0.18)' }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Audience Preview">
              <div className="px-7 py-7">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-lg font-bold text-text-primary" style={{ fontFamily: FONT }}>Audience Preview</h2>
                  <button type="button" onClick={onClose} aria-label="Close preview" className="w-8 h-8 rounded-full inline-flex items-center justify-center text-text-secondary hover:bg-text-primary/[0.06] active:scale-[0.94] transition-[background-color,transform] duration-150"><XIcon size={14} /></button>
                </div>
                {visible.length === 0 ? (
                  <p className="text-sm text-text-secondary text-center py-6">No audiences yet.</p>
                ) : (
                  <div className="flex flex-col gap-5">
                    {visible.map((aud, i) => {
                      const selectedLearners = Array.from(aud.learnerIds).map((id) => learnerById.get(id)).filter((l): l is Learner => Boolean(l));
                      return (
                        <motion.section key={aud.id} initial={reduceMotion ? false : { opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18, ease: EASE_OUT, delay: reduceMotion ? 0 : i * 0.04 }} className="rounded-xl border border-border-primary/15 px-5 py-4">
                          <header className="flex items-baseline justify-between mb-3">
                            <h3 className="text-sm font-extrabold text-text-primary tracking-wide" style={{ fontFamily: FONT }}>{aud.name.toUpperCase()}</h3>
                            <span className="text-xs text-text-secondary">{selectedLearners.length} learner{selectedLearners.length === 1 ? '' : 's'}</span>
                          </header>
                          {selectedLearners.length === 0 ? (
                            <p className="text-xs text-text-tertiary italic">No learners assigned to this audience yet.</p>
                          ) : (
                            <ul className="flex flex-col">
                              {selectedLearners.map((l) => (
                                <li key={l.id} className="flex items-baseline justify-between gap-3 py-1 text-xs">
                                  <span className="font-semibold text-text-primary truncate">{l.full_name}</span>
                                  <span className="text-text-tertiary truncate">{l.email}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </motion.section>
                      );
                    })}
                  </div>
                )}
                <div className="flex justify-end mt-6">
                  <PillButton onClick={onClose} variant="purple"><CheckIcon size={14} /> Done</PillButton>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────

export default function ProgramConfigurePage() {
  const { id: programId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setCollapsed: setSidebarCollapsed } = useSidebar();
  const {
    data: program,
    isLoading: programLoading,
    isError,
    refetch: refetchProgram,
  } = useProgram(programId);
  const { data: learners = [], isLoading: learnersLoading } = useAssignableLearners();
  const { data: roles = [], isLoading: rolesLoading } = useLearnerRoles();
  const { data: departments = [], isLoading: departmentsLoading } = useLearnerDepartments();
  const { data: locations = [], isLoading: locationsLoading } = useLearnerLocations();

  const createAudience = useCreateAudience();
  const updateAudience = useUpdateAudience();
  const deleteAudience = useDeleteAudience();
  const addLearners = useAddAudienceLearners();
  const removeLearners = useRemoveAudienceLearners();
  const sendAudienceInvite = useSendAudience();
  const updateProgramStatus = useUpdateProgramStatus();
  const [statusPending, setStatusPending] = useState(false);

  // Publishing is reachable from the detail page; it belongs here too, where
  // the program is actually being set up (Aleix, 2026-09-11), and matches the
  // course wizard exactly.
  const changeProgramStatus = async (status: 'active' | 'draft') => {
    if (!programId || statusPending) return;
    setStatusPending(true);
    try {
      await updateProgramStatus.mutateAsync({ programId, status });
      await refetchProgram();
      toast.success(status === 'active' ? 'Program published.' : 'Program moved to drafts.');
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      toast.error(message ?? 'Could not change the program status.');
    } finally {
      setStatusPending(false);
    }
  };

  const reduceMotion = useReducedMotion();
  const stepDelay = (i: number) => (reduceMotion ? 0 : i * 0.06);
  const { state, markStepSaved } = useWizardState(programId ? { courseId: programId, title: program?.name } : undefined);

  // Auto-collapse sidebar
  useEffect(() => {
    setSidebarCollapsed(true);
    return () => setSidebarCollapsed(false);
  }, [setSidebarCollapsed]);
  // Reset scroll on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ── Audience state ──────────────────────────────────────────────────────
  const [audiences, setAudiences] = useState<ProgramAudienceDraft[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [seeded, setSeeded] = useState(false);
  const [learnersModalAudienceId, setLearnersModalAudienceId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Seed from server once when program loads.
  useEffect(() => {
    if (!program || seeded) return;
    const seeds = (program.audiences ?? []).map(fromServer);
    setAudiences(seeds);
    setActiveId(seeds[0]?.id ?? '');
    setSeeded(true);
  }, [program, seeded]);

  // The program payload carries learner COUNTS only, so an existing audience
  // used to render as "No learners yet" until the picker was opened — and its
  // member chips would have been empty (Aleix, 2026-09-08). Pull each
  // audience's detail, which includes its learners, and seed both the working
  // set and the baseline the save diffs against.
  useEffect(() => {
    if (!programId || !seeded) return;
    const pending = audiences.filter((a) => a.serverId && !a.membersLoaded);
    if (pending.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const a of pending) {
        try {
          const detail = await fetchAudience({ programId, audienceId: a.serverId! });
          if (cancelled) return;
          // The audience payload keys learners by membership; every picker
          // and the add/remove endpoints speak USER ids.
          const ids = new Set(
            (detail.learners ?? [])
              .map((l) => l.membership?.user_id)
              .filter((id): id is string => Boolean(id))
          );
          setAudiences((prev) =>
            prev.map((x) =>
              x.id === a.id
                ? { ...x, learnerIds: ids, initialLearnerIds: new Set(ids), membersLoaded: true }
                : x
            )
          );
        } catch {
          // A failed fetch must not wedge the page: mark it done so the card
          // falls back to the count, and the picker can still load members.
          if (!cancelled) {
            setAudiences((prev) =>
              prev.map((x) => (x.id === a.id ? { ...x, membersLoaded: true } : x))
            );
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [programId, seeded, audiences]);

  // Keep activeId valid as audiences change
  useEffect(() => {
    const visible = audiences.filter((a) => !a.removed);
    if (visible.length === 0) return;
    if (!visible.some((a) => a.id === activeId)) setActiveId(visible[0].id);
  }, [audiences, activeId]);

  const visibleAudiences = audiences.filter((a) => !a.removed);
  const audienceReady = visibleAudiences.some((a) => a.learnerIds.size > 0);

  const addAudience = useCallback(() => {
    setAudiences((prev) => {
      const n = newAudienceDraft(prev.filter((a) => !a.removed).length + 1);
      setActiveId(n.id);
      return [...prev, n];
    });
  }, []);
  const patchAudience = useCallback((id: string, patch: Partial<ProgramAudienceDraft>) => {
    setAudiences((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  }, []);
  // One-click removal from a draft audience — the card's chips call this
  // instead of making the trainer reopen the picker (Aleix, 2026-09-08).
  const removeAudienceLearner = useCallback((audienceId: string, learnerId: string) => {
    setAudiences((prev) =>
      prev.map((a) => {
        if (a.id !== audienceId) return a;
        const learnerIds = new Set(a.learnerIds);
        learnerIds.delete(learnerId);
        return { ...a, learnerIds };
      })
    );
  }, []);

  const renameAudience = useCallback((id: string, name: string) => {
    setAudiences((prev) => prev.map((a) => (a.id === id ? { ...a, name } : a)));
  }, []);
  const removeAudience = useCallback((id: string) => {
    setAudiences((prev) => prev.map((a) => {
      if (a.id !== id) return a;
      // If never persisted, drop entirely; else mark for DELETE
      if (a.serverId === null) return { ...a, removed: true };
      return { ...a, removed: true };
    }));
  }, []);

  // ── Center-of-viewport scroll tracking ──────────────────────────────────
  const [focusedStep, setFocusedStep] = useState<ProgramStep>(1);
  const stepRefs = useRef<Record<ProgramStep, HTMLDivElement | null>>({ 1: null, 2: null, 3: null, 4: null });
  const setStepRef = useCallback((step: ProgramStep) => (el: HTMLDivElement | null) => {
    stepRefs.current[step] = el;
  }, []);
  useEffect(() => {
    const handle = () => {
      const center = window.innerHeight / 2;
      let best: ProgramStep = 1;
      let bestDist = Infinity;
      for (const step of [1, 2, 3, 4] as ProgramStep[]) {
        const el = stepRefs.current[step];
        if (!el) continue;
        const { top, bottom } = el.getBoundingClientRect();
        const c = (top + bottom) / 2;
        const d = Math.abs(c - center);
        if (d < bestDist) { bestDist = d; best = step; }
      }
      setFocusedStep(best);
    };
    window.addEventListener('scroll', handle, { passive: true });
    window.addEventListener('resize', handle, { passive: true });
    handle();
    return () => {
      window.removeEventListener('scroll', handle);
      window.removeEventListener('resize', handle);
    };
  }, []);

  // ── Final save: diff and persist ────────────────────────────────────────
  const handleFinalSave = async (send: boolean) => {
    if (!programId) return;
    if (visibleAudiences.length === 0) {
      toast.error('Add at least one audience first.');
      return;
    }
    // Per-audience validation
    for (const a of visibleAudiences) {
      const missing: string[] = [];
      if (a.learnerIds.size === 0) missing.push('learners');
      if (!a.startDate) missing.push('a start date');
      if (!a.endDate) missing.push('an end date');
      if (missing.length > 0) {
        toast.error(`${a.name} is missing ${missing.join(' & ')}.`);
        return;
      }
      if (new Date(a.endDate) < new Date(a.startDate)) {
        toast.error(`${a.name}: end date cannot be before the start date.`);
        return;
      }
      if (a.publishDate && new Date(a.publishDate) > new Date(a.startDate)) {
        toast.error(`${a.name}: publish date must be on or before the start date.`);
        return;
      }
    }

    setSaving(true);
    try {
      // 1) DELETE removed audiences that were persisted. Sent ones included:
      //    skipping them made the card vanish locally and reappear on reload
      //    (Aleix, 2026-09-08). The server revokes their access on delete.
      for (const a of audiences) {
        if (a.removed && a.serverId) {
          await deleteAudience.mutateAsync({ programId, audienceId: a.serverId });
        }
      }
      // 2) Create new audiences
      for (const a of visibleAudiences) {
        if (a.serverId === null) {
          await createAudience.mutateAsync({
            programId,
            data: {
              name: a.name,
              learner_ids: Array.from(a.learnerIds),
              start_date: a.startDate,
              end_date: a.endDate,
              publish_date: a.publishDate || undefined,
              channels: a.invitationChannels,
              invite_message: a.invitationHtml || undefined,
            },
          });
        }
      }
      // 3) Update existing audiences (name + dates only via PUT) and learner diffs.
      //    A sent audience is never PUT/patched — only its learner diff runs.
      for (const a of visibleAudiences) {
        if (!a.serverId) continue;
        // A sent audience keeps its invitation (dates, channel, message) —
        // only who receives it can still change (Aleix, 2026-09-08, matching
        // the course flow). Everything else is diffed as before.
        // Schedule (name + dates) stays editable after sending; the invitation
        // itself does not, and the server rejects those fields on a sent
        // audience — so they are never sent for one.
        const scheduleDirty =
          a.name !== a.initialName ||
          a.startDate !== a.initialStartDate ||
          a.endDate !== a.initialEndDate ||
          a.publishDate !== a.initialPublishDate;
        const invitationDirty =
          a.invitationHtml !== a.initialInvitationHtml ||
          [...a.invitationChannels].sort().join(',') !==
            [...a.initialInvitationChannels].sort().join(',');
        const dirty = scheduleDirty || (!a.sent && invitationDirty);
        if (dirty) {
          await updateAudience.mutateAsync({
            programId,
            audienceId: a.serverId,
            data: {
              name: a.name,
              start_date: a.startDate,
              end_date: a.endDate,
              publish_date: a.publishDate || undefined,
              ...(a.sent
                ? {}
                : {
                    channels: a.invitationChannels,
                    invite_message: a.invitationHtml || undefined,
                  }),
            },
          });
        }
        const added: string[] = [];
        const removed: string[] = [];
        // Guard against a stray empty/undefined id reaching the API, which
        // answers "input validation failed" for the whole save.
        a.learnerIds.forEach((id) => {
          if (id && !a.initialLearnerIds.has(id)) added.push(id);
        });
        a.initialLearnerIds.forEach((id) => {
          if (id && !a.learnerIds.has(id)) removed.push(id);
        });
        if (added.length > 0) {
          await addLearners.mutateAsync({ programId, audienceId: a.serverId, learnerIds: added });
        }
        if (removed.length > 0) {
          await removeLearners.mutateAsync({ programId, audienceId: a.serverId, learnerIds: removed });
        }
      }
      if (send) {
        // Fresh audiences were created above, so re-read their ids from the
        // server rather than the local draft. Anything already sent is left
        // alone; the endpoint only targets members still pending.
        const saved = await refetchProgram();
        const cohorts = saved.data?.audiences ?? [];
        for (const cohort of cohorts) {
          await sendAudienceInvite.mutateAsync({
            programId,
            audienceId: String(cohort.id),
          });
        }
        toast.success(
          `Invitations sent to ${visibleAudiences.length} ${visibleAudiences.length === 1 ? 'audience' : 'audiences'}.`
        );
      } else {
        toast.success(
          `Saved ${visibleAudiences.length} ${visibleAudiences.length === 1 ? 'audience' : 'audiences'} as a draft.`
        );
      }
      markStepSaved(4);
      navigate(`/programs/${programId}`);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Failed to save audiences. Please try again.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // ── Loading / error states ──
  if (programLoading) {
    return (
      <div className="p-6 lg:p-10 max-w-[1080px] mx-auto">
        <div className="h-6 w-32 rounded bg-text-tertiary/10 animate-pulse mb-8" />
        <div className="h-32 rounded-2xl bg-text-tertiary/10 animate-pulse" />
      </div>
    );
  }
  if (isError || !program) {
    return (
      <div className="p-6 lg:p-10 max-w-[640px] mx-auto text-center">
        <p className="text-sm text-error-500 mb-4">Could not load this program.</p>
        <PillButton variant="ghost" onClick={() => navigate('/programs')}>← Back to Programs</PillButton>
      </div>
    );
  }

  const learnersModalAudience = audiences.find((a) => a.id === learnersModalAudienceId) ?? null;

  // Editability is per-audience, keyed to whether that audience has already
  // been sent — NOT to the parent program status. You can always add new
  // audiences and edit ones that haven't been sent, even on an active program.
  // Each step locks its own inputs from the active audience's `sent` flag.
  const hasSentAudience = visibleAudiences.some((a) => a.sent);

  // ── Indicator wiring ────────────────────────────────────────────────────
  // Step 2 only earns a check once an audience actually has learners — the
  // wizard hook pre-marks it 'saved' in edit mode, which lies when audiences
  // are still empty.
  const indicatorSteps: Record<IndicatorStepNumber, WizardStepState> = {
    1: state.steps[1],
    2: audienceReady ? state.steps[2] : { status: 'pending' },
    3: state.steps[3],
    4: state.steps[4],
  };
  const indicatorFocused: IndicatorStepNumber = focusedStep;

  return (
    <div className="min-h-screen">
      <Toaster position="top-right" />

      <WizardStepIndicator steps={indicatorSteps} focusedStep={indicatorFocused} gamificationEnabled={true} labels={STEP_LABELS} />

      <div className="max-w-[1080px] mx-auto px-6 pt-6 flex flex-wrap items-center justify-between gap-3">
        <PillButton variant="ghost" onClick={() => navigate(`/programs/${programId}`)}>← Back to Program</PillButton>
        <div className="flex items-center gap-3">
          <p className="text-sm text-text-secondary truncate max-w-[46ch]" style={{ fontFamily: FONT }}>
            Configuring <span className="font-bold text-text-primary">{program.name}</span>
          </p>
          {/* The wizard offers "Configure"; this is the way back (Aleix, 9/8). */}
          <button
            type="button"
            onClick={() => navigate(`/programs/${programId}/edit`)}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-text-primary border border-border-primary/40 hover:bg-text-primary/[0.06] active:scale-[0.97] focus-ring transition-[background-color,transform] duration-150"
          >
            Edit program
          </button>
          <PublishStatusControl
            entity="program"
            status={program.status}
            pending={statusPending}
            onPublish={() => changeProgramStatus('active')}
            onUnpublish={() => changeProgramStatus('draft')}
          />
        </div>
      </div>

      <div className="max-w-[1080px] mx-auto px-6 pt-8 pb-24 flex flex-col gap-10">
        {hasSentAudience && (
          <div className="rounded-2xl border border-border-primary/30 bg-text-tertiary/5 px-5 py-4 text-sm text-text-secondary">
            An audience that has already been sent keeps the{' '}
            <span className="font-semibold text-text-primary">invitation it sent</span> — its
            channel and message are fixed. Its <span className="font-semibold text-text-primary">dates</span>{' '}
            and <span className="font-semibold text-text-primary">who receives it</span> can still
            change: people you add get the same invitation, and people you remove lose access to
            the program (their progress is kept).
          </div>
        )}
        <div ref={setStepRef(1)} data-configure-step="1">
          <StepCard stepNumber={1} title="Audiences" entranceDelay={stepDelay(0)}>
            <Step1Audiences
              audiences={audiences}
              learners={learners}
              onAddAudience={addAudience}
              onAddLearners={(id) => setLearnersModalAudienceId(id)}
              onRenameAudience={renameAudience}
              onRemoveAudience={removeAudience}
              onRemoveLearner={removeAudienceLearner}
            />
          </StepCard>
        </div>

        <div ref={setStepRef(2)} data-configure-step="2" className="relative">
          {!audienceReady && <LockedOverlay message="Add learners to an audience first" />}
          <StepCard stepNumber={2} title="Time Frames" disabled={!audienceReady} entranceDelay={stepDelay(1)}>
            <Step2TimeFrames audiences={audiences} activeId={activeId} setActiveId={setActiveId} patchAudience={patchAudience} durationDays={program.duration_days} />
          </StepCard>
        </div>

        <div ref={setStepRef(3)} data-configure-step="3" className="relative">
          {!audienceReady && <LockedOverlay message="Add learners to an audience first" />}
          <StepCard stepNumber={3} title="Compose an Invitation Text" disabled={!audienceReady} entranceDelay={stepDelay(2)}>
            <Step3Invitation audiences={audiences} activeId={activeId} setActiveId={setActiveId} patchAudience={patchAudience} />
          </StepCard>
        </div>

        <div ref={setStepRef(4)} data-configure-step="4" className="relative">
          {!audienceReady && <LockedOverlay message="Add learners to an audience first" />}
          <StepCard stepNumber={4} title="Publishing" disabled={!audienceReady} entranceDelay={stepDelay(3)}>
            <Step4Publishing audiences={audiences} activeId={activeId} setActiveId={setActiveId} patchAudience={patchAudience} />
          </StepCard>
        </div>

        {/* Single bottom action bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
          <PillButton variant="ghost" onClick={() => navigate(`/programs/${programId}`)}>
            <XIcon size={14} /> Cancel
          </PillButton>
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <PillButton variant="orange-outline" onClick={() => setPreviewOpen(true)} disabled={visibleAudiences.length === 0}>
              <EyeOpenIcon /> Preview
            </PillButton>
            <PillButton
              variant="ghost"
              onClick={() => handleFinalSave(false)}
              disabled={!audienceReady || saving}
            >
              <PenIcon /> Save as draft
            </PillButton>
            <PillButton
              variant="purple"
              onClick={() => handleFinalSave(true)}
              disabled={!audienceReady || saving}
            >
              <CheckIcon size={14} />
              {saving ? 'Saving…' : 'Save & send'}
            </PillButton>
          </div>
        </div>
      </div>

      {/* Add Learners modal */}
      <AddLearnersModal
        isOpen={learnersModalAudience !== null}
        onClose={() => setLearnersModalAudienceId(null)}
        audience={learnersModalAudience}
        learners={learners}
        roles={roles}
        departments={departments}
        locations={locations}
        learnersLoading={learnersLoading}
        rolesLoading={rolesLoading}
        departmentsLoading={departmentsLoading}
        locationsLoading={locationsLoading}
        onApply={(learnerIds) => {
          if (!learnersModalAudience) return;
          patchAudience(learnersModalAudience.id, { learnerIds });
        }}
      />

      {/* Audience Preview */}
      <AudiencePreviewModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        audiences={audiences}
        learners={learners}
      />
    </div>
  );
}
